/**
 * Service de gestion des budgets par catégorie (web)
 */

import { supabase } from '../supabaseClient';

export interface BudgetConsommation {
  categorie_id: string;
  categorie_nom: string;
  categorie_couleur: string;
  categorie_icone?: string | null;
  budget_id: string | null;
  montant_alloue: number;
  montant_depense: number;
  pourcentage_consomme: number;
  depassement: boolean;
  montant_depassement: number;
}

export type BudgetProgressStatus = 'none' | 'ok' | 'warning' | 'exceeded';

const MONTHS = [
  'janvier', 'février', 'mars', 'avril', 'mai', 'juin',
  'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre',
];

export function monthToDate(monthIndex: number, year: number): string {
  const month = String(monthIndex + 1).padStart(2, '0');
  return `${year}-${month}-01`;
}

export function monthNameFromIndex(monthIndex: number): string {
  return MONTHS[monthIndex] ?? 'janvier';
}

export function formatMontant(value: number): string {
  return `${value.toLocaleString('fr-FR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} €`;
}

export function getBudgetProgressStatus(
  montantAlloue: number,
  pourcentageConsomme: number,
  depassement: boolean,
): BudgetProgressStatus {
  if (montantAlloue <= 0) return 'none';
  if (depassement || pourcentageConsomme >= 100) return 'exceeded';
  if (pourcentageConsomme >= 75) return 'warning';
  return 'ok';
}

export function getBudgetProgressClass(status: BudgetProgressStatus): string {
  switch (status) {
    case 'exceeded':
      return 'bg-red-500';
    case 'warning':
      return 'bg-amber-500';
    case 'ok':
      return 'bg-primary-500';
    default:
      return 'bg-dark-600';
  }
}

export async function getBudgetsConsommation(
  mois: string,
): Promise<{ data: BudgetConsommation[] | null; error: unknown }> {
  try {
    const { getImpersonatedUserIdFromStorage } = await import(
      '../../contexts/ImpersonationContext'
    );
    const impersonatedUserId = getImpersonatedUserIdFromStorage();
    const { data, error } = await supabase.rpc('get_budgets_consommation', {
      p_mois: mois,
      ...(impersonatedUserId ? { p_user_id: impersonatedUserId } : {}),
    });

    if (error) {
      console.error('[budgetsService] get_budgets_consommation:', error);
      return { data: null, error };
    }

    const rows = (data as BudgetConsommation[] | null) ?? [];
    return {
      data: rows.map((row) => ({
        ...row,
        montant_alloue: Number(row.montant_alloue ?? 0),
        montant_depense: Number(row.montant_depense ?? 0),
        pourcentage_consomme: Number(row.pourcentage_consomme ?? 0),
        montant_depassement: Number(row.montant_depassement ?? 0),
        depassement: Boolean(row.depassement),
      })),
      error: null,
    };
  } catch (error) {
    console.error('[budgetsService] Exception getBudgetsConsommation:', error);
    return { data: null, error };
  }
}

export async function upsertBudget(
  categorieId: string,
  mois: string,
  montantAlloue: number,
): Promise<{ data: string | null; error: unknown }> {
  try {
    const { getImpersonatedUserIdFromStorage } = await import(
      '../../contexts/ImpersonationContext'
    );
    const impersonatedUserId = getImpersonatedUserIdFromStorage();
    const { data, error } = await supabase.rpc('upsert_budget', {
      p_categorie_id: categorieId,
      p_mois: mois,
      p_montant_alloue: montantAlloue,
      ...(impersonatedUserId ? { p_user_id: impersonatedUserId } : {}),
    });

    if (error) {
      console.error('[budgetsService] upsert_budget:', error);
      return { data: null, error };
    }

    return { data: data as string, error: null };
  } catch (error) {
    console.error('[budgetsService] Exception upsertBudget:', error);
    return { data: null, error };
  }
}

export interface BudgetSuggestion {
  categorie_id: string;
  nom: string;
  couleur?: string | null;
  moyenne_3mois: number;
  suggestion: number;
}

export async function suggestBudgets(
  reductionPct = 10,
): Promise<{ data: BudgetSuggestion[] | null; error: unknown }> {
  try {
    const { getImpersonatedUserIdFromStorage } = await import(
      '../../contexts/ImpersonationContext'
    );
    const impersonatedUserId = getImpersonatedUserIdFromStorage();
    const { data, error } = await supabase.rpc('suggest_budgets', {
      p_reduction_pct: reductionPct,
      ...(impersonatedUserId ? { p_user_id: impersonatedUserId } : {}),
    });

    if (error) {
      console.error('[budgetsService] suggest_budgets:', error);
      return { data: null, error };
    }

    const rows = (data as BudgetSuggestion[] | null) ?? [];
    return {
      data: rows.map((row) => ({
        ...row,
        moyenne_3mois: Number(row.moyenne_3mois ?? 0),
        suggestion: Number(row.suggestion ?? 0),
      })),
      error: null,
    };
  } catch (error) {
    console.error('[budgetsService] Exception suggestBudgets:', error);
    return { data: null, error };
  }
}

/** Revenu moyen mensuel sur les 3 mois calendaires précédents. */
export async function getAverageMonthlyIncome(): Promise<{
  data: number;
  error: unknown;
}> {
  try {
    const now = new Date();
    const end = new Date(now.getFullYear(), now.getMonth(), 1);
    const start = new Date(end.getFullYear(), end.getMonth() - 3, 1);
    const startIso = start.toISOString().slice(0, 10);
    const endIso = end.toISOString().slice(0, 10);

    const { getImpersonatedUserIdFromStorage } = await import(
      '../../contexts/ImpersonationContext'
    );
    const impersonatedUserId = getImpersonatedUserIdFromStorage();

    let rows: { montant?: number | null; type?: string | null; date?: string }[] = [];

    if (impersonatedUserId) {
      const rpc = await supabase.rpc('admin_get_transactions', {
        p_user_id: impersonatedUserId,
      });
      if (rpc.error) return { data: 0, error: rpc.error };
      rows = ((rpc.data as typeof rows) || []).filter(
        (t) =>
          t.type === 'revenu' &&
          typeof t.date === 'string' &&
          t.date >= startIso &&
          t.date < endIso,
      );
    } else {
      const { data, error } = await supabase
        .from('transactions')
        .select('montant, type, date')
        .gte('date', startIso)
        .lt('date', endIso)
        .eq('type', 'revenu');
      if (error) return { data: 0, error };
      rows = data || [];
    }

    const total = rows.reduce((sum, t) => sum + Math.abs(Number(t.montant) || 0), 0);
    return { data: Math.round((total / 3) * 100) / 100, error: null };
  } catch (error) {
    return { data: 0, error };
  }
}

export async function upsertBudgetsBatch(
  mois: string,
  items: { categorie_id: string; montant: number }[],
): Promise<{ success: boolean; error?: unknown }> {
  for (const item of items) {
    const { error } = await upsertBudget(item.categorie_id, mois, item.montant);
    if (error) return { success: false, error };
  }
  return { success: true };
}
